import { StyleSheet, Text, View } from 'react-native';

import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import type { LeaderboardEntry } from '@/types/leaderboard';

import { Avatar } from './Avatar';
import { MoneyDisplay } from './MoneyDisplay';

export type PodiumVariant = 'gold' | 'silver' | 'bronze';

export interface PodiumCardProps {
  entry: LeaderboardEntry;
  variant: PodiumVariant;
  /** Highlight as the signed-in user with an Electric Green outer ring. */
  isMe?: boolean;
  testID?: string;
}

// Bronze has no token (single-use across the app); silver reuses Cool Blue;
// gold reuses Electric Green per the brand voice rule "Electric Green earns
// one moment per screen" — the #1 spot is that moment. Documented in
// `docs/screens/leaderboard.md` Drift table.
const BRONZE = '#C77B3F';

const VARIANT_ACCENT: Record<PodiumVariant, string> = {
  gold: colors.electricGreen,
  silver: colors.coolBlue,
  bronze: BRONZE,
};

const VARIANT_BAR_HEIGHT: Record<PodiumVariant, number> = {
  gold: 160,
  silver: 120,
  bronze: 100,
};

const VARIANT_FLEX: Record<PodiumVariant, number> = {
  gold: 1.1,
  silver: 1,
  bronze: 1,
};

export function PodiumCard({ entry, variant, isMe, testID }: PodiumCardProps) {
  const accent = VARIANT_ACCENT[variant];
  const barHeight = VARIANT_BAR_HEIGHT[variant];
  const flex = VARIANT_FLEX[variant];
  // `||` (not `??`): empty-string display_name from a backend bug should fall
  // back to the full display_name and ultimately the em-dash placeholder, not
  // render an empty card.
  const firstName =
    entry.display_name.trim().split(/\s+/)[0] || entry.display_name || '—';
  const initials = initialsFor(entry.display_name);

  return (
    <View
      testID={testID}
      style={[styles.outer, { flex }, isMe && styles.outerMe]}
      accessible
      accessibilityRole="text"
      accessibilityLabel={[
        `Rank ${entry.rank}`,
        entry.display_name,
        `${formatMoneyForA11y(entry.month_cents)} from referrals`,
        isMe ? "that's you" : null,
      ]
        .filter(Boolean)
        .join(', ')}
    >
      <View style={styles.headerStack}>
        <View style={[styles.avatarRing, { borderColor: accent }]}>
          <Avatar size={48} initials={initials} tone="frost" />
        </View>
        <Text style={styles.name} numberOfLines={1} ellipsizeMode="tail">
          {firstName}
        </Text>
        <MoneyDisplay cents={entry.month_cents} size="sm" />
      </View>
      <View style={[styles.bar, { height: barHeight }]}>
        <Text style={[styles.rank, { color: accent }]}>{entry.rank}</Text>
      </View>
    </View>
  );
}

function initialsFor(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  const first = parts[0];
  const second = parts[1];
  if (first && second) {
    // Codepoint-aware indexing so emoji / CJK / surrogate-pair names don't
    // produce malformed half-character strings.
    const firstChar = [...first][0] ?? '';
    const secondChar = [...second][0] ?? '';
    return (firstChar + secondChar).toUpperCase();
  }
  if (first) {
    const chars = [...first].slice(0, 2).join('');
    return chars.toUpperCase();
  }
  return '?';
}

export function formatMoneyForA11y(cents: number): string {
  const dollars = Math.floor(Math.abs(cents) / 100);
  const remainder = Math.abs(cents) % 100;
  const sign = cents < 0 ? '-' : '';
  if (remainder === 0) return `${sign}$${dollars}`;
  return `${sign}$${dollars}.${remainder.toString().padStart(2, '0')}`;
}

const styles = StyleSheet.create({
  outer: {
    alignItems: 'stretch',
  },
  outerMe: {
    padding: 1,
    borderRadius: radii.lg + 2,
    borderWidth: 2,
    borderColor: colors.electricGreen,
  },
  headerStack: {
    alignItems: 'center',
    paddingBottom: spacing.xs,
  },
  avatarRing: {
    borderRadius: radii.full,
    borderWidth: 3,
    overflow: 'hidden',
    backgroundColor: colors.frost,
    marginBottom: spacing.xs,
  },
  name: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xs,
    color: colors.deepGreen,
    textAlign: 'center',
    maxWidth: '100%',
    paddingHorizontal: spacing.xs,
    marginBottom: 2,
  },
  bar: {
    backgroundColor: colors.deepGreen,
    borderTopLeftRadius: radii.lg,
    borderTopRightRadius: radii.lg,
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBottom: spacing.md,
  },
  rank: {
    fontFamily: fonts.numericHeavy,
    fontSize: 38, // matches handoff `leaderboard.jsx:229`; not in fontSizes scale
    lineHeight: 38,
  },
});
